import { useMemo } from 'react';
import { AppointmentType } from '@weave/schema-gen-ts/dist/schemas/schedule/v3/appointment_type.pb';
import dayjs from 'dayjs';
import { convertEventTimesFromUTC } from '@frontend/schedule-helpers';
import { ScheduleRequestEvent } from '../../types';
import { useGetScheduleRequestListQueries } from '../../views/Calendar/EventsPanel/ScheduleActions/ScheduleRequests/hooks/useGetScheduleRequestListQueries';
import { useSchedulingLocationInfo } from '../use-scheduling-location-info';
import { useConvertScheduleRequestToEvent } from './use-convert-schedule-request-to-event';

type UseCalendarScheduleRequestsParams = {
  locationIds: string[];
  calendarDate: string;
  appointmentTypes?: AppointmentType[];
};

export const useCalendarScheduleRequests = ({
  locationIds,
  calendarDate,
  appointmentTypes,
}: UseCalendarScheduleRequestsParams) => {
  const { getLocationTimeZone } = useSchedulingLocationInfo();
  const { scheduleRequests, refetch } = useGetScheduleRequestListQueries({ locationIds, date: calendarDate });
  const { scheduleRequestEventsData } = useConvertScheduleRequestToEvent(scheduleRequests, appointmentTypes);

  const scheduledRequestsWithConvertedTimes: ScheduleRequestEvent[] = useMemo(() => {
    const calendarDay = dayjs(calendarDate, 'MM/DD/YYYY');

    return (scheduleRequestEventsData ?? []).reduce<ScheduleRequestEvent[]>((acc, sr: ScheduleRequestEvent) => {
      const locationTimeZone = getLocationTimeZone(sr.locationId ?? '');

      const converted = convertEventTimesFromUTC(sr, locationTimeZone);
      const eventStartDay = dayjs(converted.startDate);
      const eventEndDay = dayjs(converted.endDate);

      // Keep only events whose date range overlaps with calendarDate (handles previous/next day bleed-over).
      const isOnCalendarDate = !calendarDay.isBefore(eventStartDay, 'day') && !calendarDay.isAfter(eventEndDay, 'day');

      if (isOnCalendarDate) {
        const allRequestedOpenings =
          sr.request.allRequestedOpenings?.map((opening) => convertEventTimesFromUTC(opening, locationTimeZone)) ?? [];
        const otherRequestedOpenings =
          sr.request.otherRequestedOpenings?.map((opening) => convertEventTimesFromUTC(opening, locationTimeZone)) ??
          [];
        acc.push({
          ...converted,
          request: { ...sr.request, allRequestedOpenings, otherRequestedOpenings },
        });
      }

      return acc;
    }, []);
  }, [scheduleRequestEventsData, getLocationTimeZone, calendarDate]);

  return {
    scheduleRequests,
    scheduleRequestEventsData: scheduledRequestsWithConvertedTimes,
    refetchScheduleRequestList: refetch,
  };
};
